import { Component, OnDestroy, OnInit } from '@angular/core';
import {
    AbstractControl,
    ReactiveFormsModule,
    FormGroup,
    FormControl, FormArray, Validators
} from '@angular/forms';
import { combineLatest, interval, Observable, Subscription } from 'rxjs';
import { CurrencyService } from '../../shared/services/currency.service';
import { countries } from '../../../list/countries';
import { AppState } from '../../../store';
import { Store } from '@ngrx/store';
import {
    selectQuickQuote, selectShipmentEditorCategory,
    selectShipmentEditorDeclaration, selectShipmentEditorHasInsurance,
    selectShipmentEditorHasOption, selectShipmentEditorOptions, selectShipmentEditorShipment
} from '../../../store/shipment-editor/shipment-editor.state';
import { debounceTime, delay, delayWhen, map, take } from 'rxjs/operators';
import { IconComponent } from '../../shared/icon/icon.component';
import { MatIconButton, MatButton, MatIconAnchor } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDivider } from '@angular/material/divider';
import { MatOption } from '@angular/material/core';
import { AsyncPipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { MatSelect } from '@angular/material/select';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { selectPackagingTypes } from '../../../store/profile/profile.state';
import { Shipment } from '../../../models/shipment';
import { Commodity } from '../../../models/commodity';
import { Package } from '../../../models/package';
import { emptyShipment } from '../../../constants/empty-shipment';
import { emptyPackage } from '../../../constants/empty-package';
import {
    shipmentEditorAddCommodity, shipmentEditorAddEmptyPackage,
    shipmentEditorAddPackage,
    shipmentEditorDeleteCommodity,
    shipmentEditorDeletePackage,
    shipmentEditorDeletePackages, shipmentEditorDeTemplateCommodity, shipmentEditorDeTemplatePackage, shipmentEditorSetCurrency,
    shipmentEditorSetInsurance, shipmentEditorSetOption,
    shipmentEditorSetPackagingType,
    shipmentEditorSetWeightUnit,
    shipmentEditorUpdatePackageSection
} from '../../../store/shipment-editor/shipment-editor.actions';
import { emptyCommodity } from '../../../constants/empty-commodity';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { WeightUnitEnum } from '../../../enum/weight-unit.enum';
import { WeightPipe } from '../../shared/pipes/weight.pipe';
import { DimensionsPipe } from '../../shared/pipes/dimensions.pipe';
import { CountryNamePipe } from '../../shared/pipes/country-name.pipe';
import { CountryFieldComponent } from '../../shared/country-field/country-field.component';
import { IconEnum } from '../../../enum/icon.enum';
import { PackagingTypeEnum } from '../../../enum/packaging-type.enum';
import { ShipmentCategoryEnum } from '../../../enum/shipment-category.enum';
import { ShipmentPackagesTableComponent } from '../shipment-packages-table/shipment-packages-table.component';
import { ShipmentOption } from '../../../enum/shipment-option.enum';

@Component({
    selector: 'app-shipment-packages',
    templateUrl: './shipment-packages.component.html',
    styleUrls: ['./shipment-packages.component.scss'],
    imports: [ ReactiveFormsModule, MatCheckbox, MatButton, AsyncPipe, MatIconAnchor, MatMenu, MatMenuItem, MatMenuTrigger,
        ShipmentPackagesTableComponent ]
})
export class ShipmentPackagesComponent implements OnInit, OnDestroy {

    countries = countries;

    quickQuote$: Observable<boolean>;

    formGroup: FormGroup;
    disabled = false;
    formSubscription: Subscription;
    stateSubscription: Subscription;

    packageTemplates: number[] = [];
    commodityTemplates: number[] = [];

    packageSelection: number[] = [];

    constructor(private currencyService: CurrencyService,
                private store: Store<AppState>) {
        this.quickQuote$ = this.store.select(selectQuickQuote);

        this.formGroup = this.createFormGroup({
            ...emptyShipment, packages: [ { ...emptyPackage, commodities: [ emptyCommodity ] } ]
        });
        this.stateSubscription = this.store.select(selectShipmentEditorShipment).subscribe(shipment => {
            this.updateFormShape(shipment);
            this.formGroup.patchValue(shipment, { emitEvent: false });
            this.packageTemplates = shipment.packages
                .map((p, i) => ({ i, t: !!p.templateId }))
                .filter((p) => p.t)
                .map(p => p.i);
        });
        this.formSubscription = this.formGroup.valueChanges.subscribe(value => {
            this.store.dispatch(shipmentEditorUpdatePackageSection(value));
        });
    }

    insurance$ = this.store.select(selectShipmentEditorHasInsurance);

    commodityEditor$ = combineLatest([
        this.store.select(selectShipmentEditorDeclaration),
        this.store.select(selectQuickQuote)
    ]).pipe(
        // we want to display commodities if we need to declare, and we aren't in quick quote mode
        map(([ declaration, quickQuote ]): boolean => declaration && !quickQuote)
    );

    packagingTypes$ = this.store.select(selectPackagingTypes).pipe(
        map(packagingTypes => packagingTypes.filter(p => p.code !== PackagingTypeEnum.PALLET))
    );
    displayPackagingTypes$ = this.store.select(selectShipmentEditorCategory).pipe(
        map(category => category === ShipmentCategoryEnum.PARCEL)
    );
    displayLtlOptions$ = this.store.select(selectShipmentEditorCategory).pipe(
        map(category => category === ShipmentCategoryEnum.LTL)
    );
    displayDocumentsOnly$ = this.store.select(selectShipmentEditorCategory).pipe(
        map(category => category === ShipmentCategoryEnum.PARCEL)
    );
    shipment$ = this.store.select(selectShipmentEditorShipment);
    totalActualWeight$ = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => s.packages.map(p => p.quantity * p.weight)),
        map(p => p.length ? p : [ 0 ]),
        map(p => p.reduce((acc, cur) => acc + cur))
    );
    totalDimensionalWeight$ = this.shipment$.pipe(
        map(s => s.packages.map(p => (p.width * p.height * p.length) / (s.weightUnit === 'KG' ? 5000 : 139))),
        map(p => p.length ? p : [ 0 ]),
        map(p => p.reduce((acc, cur) => acc + cur))
    );
    dimensionalWeightGreater$ = combineLatest([ this.totalActualWeight$, this.totalDimensionalWeight$ ]).pipe(
        map(([ w, d ]) => d > w)
    );
    totalInsurance$ = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => s.packages.map(p => p.value)),
        map(p => p.length ? p : [ 0 ]),
        map(p => p.reduce((acc, cur) => +acc + +cur))
    );
    currentPackagingLabel$ = combineLatest([this.packagingTypes$, this.shipment$]).pipe(
        map(([packagingTypes, shipment]) => packagingTypes.find(p => p.code === shipment.packagingType)),
        map(p => p ? p.caption : $localize`Select Packaging`)
    );
    currentCurrency$ = this.shipment$.pipe(
        map(s => s.currency)
    );
    currentUnitLabel$ = this.shipment$.pipe(
        map(s => s.weightUnit),
        map(w => w === WeightUnitEnum.KG ? $localize`KG / CM` : $localize`LB / IN`)
    );
    shipmentPackages$ = this.shipment$.pipe(
        map(s => s.packages),
    );
    hazardousMaterials$ = this.store.select(selectShipmentEditorHasOption(ShipmentOption.HAZARDOUS_MATERIALS));
    refrigeratedTruck$ = this.store.select(selectShipmentEditorHasOption(ShipmentOption.REFRIGERATED_TRUCK));
    truckTypeDisplay$ = this.refrigeratedTruck$.pipe(
        map(r => r ? $localize`Reefer` : $localize`Dry LTL`)
    );

    updateFormShape(shipment: Shipment): void {
        const formPackages = this.formGroup.get('packages') as FormArray;
        while (formPackages.controls.length < shipment.packages.length) {
            formPackages.push(this.createPackageControl({
                ...emptyPackage, commodities: [ emptyCommodity ]
            }), { emitEvent: false });
        }
        while (formPackages.controls.length > shipment.packages.length) {
            formPackages.removeAt(formPackages.controls.length - 1, { emitEvent: false });
        }
        for (let i = 0; i < shipment.packages.length; i++) {
            const shipmentPackage = shipment.packages[i];
            const formPackage = formPackages.at(i) as FormGroup;
            const formCommodities = formPackage.get('commodities') as FormArray;
            while (formCommodities.controls.length < shipmentPackage.commodities.length) {
                formCommodities.push(this.createCommodityControl(emptyCommodity), { emitEvent: false });
            }
            while (formCommodities.controls.length > shipmentPackage.commodities.length) {
                formCommodities.removeAt(formCommodities.controls.length - 1, { emitEvent: false });
            }
        }
    }

    createCommodityControl(shipmentCommodity: Commodity): FormGroup {
        return new FormGroup({
            description: new FormControl(shipmentCommodity.description, { updateOn: 'blur' }),
            country: new FormControl(shipmentCommodity.country),
            quantity: new FormControl(shipmentCommodity.quantity, { updateOn: 'blur' }),
            unitOfMeasure: new FormControl(shipmentCommodity.unitOfMeasure),
            province: new FormControl(shipmentCommodity.province),
            netCost: new FormControl(shipmentCommodity.netCost, { updateOn: 'blur' }),
            markings: new FormControl(shipmentCommodity.markings, { updateOn: 'blur' }),
            weight: new FormControl(shipmentCommodity.weight, { updateOn: 'blur' }),
            producer: new FormControl(shipmentCommodity.producer),
            harmonizedCode: new FormControl(shipmentCommodity.harmonizedCode, { updateOn: 'blur' }),
            preference: new FormControl(shipmentCommodity.preference),
            weightUnit: new FormControl(shipmentCommodity.weightUnit),
            pieces: new FormControl(shipmentCommodity.pieces, { updateOn: 'blur' }),
            price: new FormControl(shipmentCommodity.price, { updateOn: 'blur' }),
            currency: new FormControl(shipmentCommodity.currency),
        });
    }

    createPackageControl(shipmentPackage: Package): FormGroup {
        return new FormGroup({
            description: new FormControl(shipmentPackage.description, { updateOn: 'blur' }),
            quantity: new FormControl(shipmentPackage.quantity, { updateOn: 'blur' }),
            weight: new FormControl(shipmentPackage.weight, { updateOn: 'blur' }),
            width: new FormControl(shipmentPackage.width, { updateOn: 'blur' }),
            height: new FormControl(shipmentPackage.height, { updateOn: 'blur' }),
            length: new FormControl(shipmentPackage.length, { updateOn: 'blur' }),
            value: new FormControl(shipmentPackage.value, { updateOn: 'blur' }),
            commodities: new FormArray(shipmentPackage.commodities.map(c => this.createCommodityControl(c))),
            packageCode: new FormControl(shipmentPackage.packageCode)
        });
    }

    createFormGroup(shipment: Shipment): FormGroup {
        return new FormGroup({
            documentsOnly: new FormControl(shipment.documentsOnly),
            packagingType: new FormControl(shipment.packagingType),
            weightUnit: new FormControl(shipment.weightUnit),
            currency: new FormControl(shipment.currency),
            packages: new FormArray(shipment.packages.map(p => this.createPackageControl(p)))
        });
    }

    isMetric(): boolean {
        return this.formGroup.get('weightUnit').value === WeightUnitEnum.KG;
    }

    canDeletePackage(): boolean {
        return this.getPackageArray().length > 1;
    }

    canDeleteCommodity(packageIndex: number): boolean {
        const packagesArray = this.formGroup.get('packages') as FormArray;
        const control = packagesArray.at(packageIndex).get('commodities') as FormArray;
        return control.length > 1;
    }

    getPackageArray(): AbstractControl[] {
        const array = this.formGroup.get('packages') as FormArray;
        return array ? array.controls : [];
    }

    packageIndices(): number[] {
        const array = this.formGroup.get('packages') as FormArray;
        return Array(array.controls.length).fill(0).map((_, i) => i);
    }

    commodityIndices(packageIndex: number): number[] {
        const array = this.formGroup.get('packages') as FormArray;
        const commodities = array.at(packageIndex)?.get('commodities') as FormArray;
        if (commodities) {
            return Array(commodities.controls.length).fill(0).map((_, i) => i);
        }
        return [];
    }

    ngOnInit(): void {
    }

    addPackage(): void {
        this.store.dispatch(shipmentEditorAddPackage());
    }

    deletePackage(packageIndex: number): void {
        this.store.dispatch(shipmentEditorDeletePackage({ packageIndex }));
    }

    addCommodity(packageIndex: number): void {
        this.store.dispatch(shipmentEditorAddCommodity({ packageIndex }));
    }

    deleteCommodity(packageIndex: number, commodityIndex: number): void {
        this.store.dispatch(shipmentEditorDeleteCommodity({ packageIndex, commodityIndex }));
    }

    currencyPrefix(): string {
        return this.currencyService.prefix(this.formGroup.get('currency').value);
    }

    ngOnDestroy(): void {
        if (this.formSubscription) {
            this.formSubscription.unsubscribe();
            this.formSubscription = null;
        }
        if (this.stateSubscription) {
            this.stateSubscription.unsubscribe();
            this.stateSubscription = null;
        }
    }

    changeInsurance($event: MatCheckboxChange): void {
        this.store.dispatch(shipmentEditorSetInsurance({ insurance: $event.checked }));
    }

    changePackagingType(packagingType: PackagingTypeEnum): void {
        this.store.dispatch(shipmentEditorSetPackagingType({ packagingType }));
    }

    changeUnits(weightUnit: string): void {
        this.store.dispatch(shipmentEditorSetWeightUnit({ weightUnit }));
    }

    changeCurrency(currency: string): void {
        this.store.dispatch(shipmentEditorSetCurrency({ currency }));
    }

    deTemplatePackage(packageIndex: number): void {
        this.store.dispatch(shipmentEditorDeTemplatePackage({ packageIndex }));
    }

    deTemplateCommoditiesPackage(packageIndex: number, commodityIndex: number): void {
        this.store.dispatch(shipmentEditorDeTemplateCommodity({ packageIndex, commodityIndex }));
    }

    trackPackage(index: number, pkg: Package): string {
        return pkg.id || index.toString();
    }

    trackCommodity(index: number, com: Commodity): string {
        return com.id || index.toString();
    }

    getCommodityCountryControl(pkgIndex: number, comIndex: number): FormControl {
        const packages = this.formGroup.get('packages') as FormArray;
        const commodities = packages.at(pkgIndex).get('commodities') as FormArray;
        return commodities.at(comIndex).get('country') as FormControl;
    }


    updatePackageSelection(selection: number[]): void {
        this.packageSelection = selection;
    }

    setHazardousMaterials(checked: boolean): void {
        this.store.dispatch(shipmentEditorSetOption({ option: ShipmentOption.HAZARDOUS_MATERIALS, value: checked }));
    }

    setRefrigeratedTruck(checked: boolean): void {
        this.store.dispatch(shipmentEditorSetOption({ option: ShipmentOption.REFRIGERATED_TRUCK, value: checked }));
    }
}
